
import singleProjectService from '@/logic/services/singleProjectService';
import contractsService from '@/logic/services/contractsService';
import router from '@/router';
import AssessmentRequestData from '@/components/AssessmentRequestData.vue';
import EazyHeading from '@/components/EazyHeading.vue';
import store from '@/store';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';

export default {
  name: 'AssessorRequestConf',

  components: {
    AssessmentRequestData,
    EazyHeading,
  },

  props: {
    inModal: Boolean,
    projectIdFromModal: {
      type: String,
      default: '',
    },
    projectStatusFromModal: {
      type: String,
      default: '',
    },
  },

  data(): Record<string, unknown> {
    return {
      router,
      device: store.state.device,
      assessmentRequestConfirmation: {
        rejectionReason: '',
      },
      validationRules: {
        rejectionReason: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      assessorIsDeclining: false,
      assessorIsInterested: false,
      doNotSendGetRequests: false,
    };
  },

  computed: {
    singleProject(): unknown {
      return store.state.singleProject;
    },

    fetchingSingleProject(): boolean {
      return store.state.fetchingSingleProject;
    },

    projectId(): string {
      return this.$route.query.id || this.projectIdFromModal;
    },

    projectStatus(): string {
      return this.projectStatusFromModal;
    },

    assessorCanAcceptOrDecline(): boolean {
      return (
        this.singleProject.status === 'pending' ||
        (this.singleProject.status === 'accepted' &&
          this.singleProject.assessor_status === 'invited')
      );
    },

    personalizedContract(): unknown {
      return store.state.contracts.shortContract;
    },

    fechingPersonalizedContract(): boolean {
      return store.state.contracts.fetchingContract;
    },

    assessorStatus(): string {
      return this.singleProject?.assessor_status;
    },

    modalOpen(): boolean {
      return store.state.modalOpen;
    },

    hasAllContractDetails(): boolean {
      return store.state.hasAllContractDetails;
    },

    canGetSingleProjectRequest(): boolean {
      return !Object.keys(this.singleProject).length && !this.fetchingSingleProject;
    },

    canGetPersonalizedContract(): boolean {
      return !this.personalizedContract && !this.fechingPersonalizedContract;
    },
  },

  watch: {
    projectId(): void {
      this.getNeededData();
    },

    projectStatus(): void {
      this.getNeededData();
    },

    assessorStatus(): void {
      if (this.assessorStatus) this.getNeededData();
    },

    modalOpen(): void {
      if (this.modalOpen && this.projectId) this.getNeededData();
    },
  },

  mounted(): void {
    if (!this.projectId) return;
    this.getNeededData();
  },

  beforeDestroy(): void {
    this.cleanProjectData();
  },

  methods: {
    getNeededData(): void {
      if (this.doNotSendGetRequests) return;

      this.doNotSendGetRequests = true;

      if (this.canGetSingleProjectRequest) this.getSingleProjectRequest();
      if (this.canGetPersonalizedContract) contractsService.getPersonalizedContract(this);

      setTimeout(() => {
        this.doNotSendGetRequests = false;
      });
    },

    getSingleProjectRequest(): void {
      singleProjectService.getSingleProject({ self: this, projectId: this.projectId });
    },

    runConfirmSingleProjectContractLogic(): void {
      if (!this.hasAllContractDetails) {
        this.remindOfMissingContractDetails();
        return;
      }

      if (!this.personalizedContract) {
        this.performConfirmSingleProjectCall();
        return;
      }

      this.$confirm(this.personalizedContract, this.$t('Terms&Conditions'), {
        confirmButtonText:
          this.device === 'desktop' ? this.$t('T&CConfirmationBtnTxt') : this.$t('accept'),
        cancelButtonText: this.$t('cancel'),
        type: 'info',
        dangerouslyUseHTMLString: true,
      })
        .then(async () => {
          this.performConfirmSingleProjectCall();
        })
        .catch((err: Error) => err);
    },

    async performConfirmSingleProjectCall(): Promise<void> {
      const resp = await singleProjectService.confirmSingleProject({
        self: this,
        projectId: this.projectId,
      });

      if (resp.ok) {
        this.$message.success(this.$t('assessorAcceptationInfo'));
        this.inModal ? this.$emit('changeStage') : this.goBackToTheDashboard();
      }
    },

    goBackToTheDashboard(): void {
      router.push('/assessor-tasks');

      setTimeout(() => {
        store.commit('setReloadSpecificList', 'projects');
      }, 1000);
    },

    async rejectSingleProjectRequest(): Promise<void> {
      formValidators.validateForm(this, 'assessmentRequestConfirmationForm');

      if (!this.formIsValid) return;

      const decision = this.assessorIsInterested ? 'interested' : 'decline';

      const resp = await singleProjectService.rejectSingleProject({
        self: this,
        projectId: this.projectId,
        rejectionReason: this.assessmentRequestConfirmation.rejectionReason,
        decision,
      });

      if (resp.ok) {
        this.$message(
          this.$t(
            decision === 'decline'
              ? 'thisProjectHasBeenDeclined'
              : 'youHaveExpressedInterestInThisProject',
          ),
        );

        !this.inModal ? router.push('/assessor-tasks') : this.$emit('changeStage');
      }
    },

    goToProfile(): void {
      store.commit('setLastVisitedProject', this.projectId);
      router.push('/assessor-profile');
    },

    cleanProjectData(): void {
      store.commit('setSingleProject', {});
      store.commit('contracts/setShortContract', '');
    },

    remindOfMissingContractDetails(): void {
      this.$confirm(this.$t('missingContractDetailsCopy'), this.$t('info'), {
        confirmButtonText: 'OK',
        cancelButtonText: this.$t('cancel'),
        type: 'info',
      })
        .then(() => {
          this.goToProfile();
        })
        .catch((err: Error) => err);
    },
  },
};
